import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash, Pencil } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const empty = () => ({ id: '', name: '', description: '', unit_price: 0, hsn_sac: '' });

export const ProductsPage = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');

  const load = async () => {
    try { setList((await axios.get(`${API_URL}/api/products`, { withCredentials: true })).data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, unit_price: parseFloat(form.unit_price) || 0 };
    if (edit) await axios.put(`${API_URL}/api/products/${form.id}`, payload, { withCredentials: true });
    else await axios.post(`${API_URL}/api/products`, payload, { withCredentials: true });
    setOpen(false); setForm(empty()); setEdit(false); load();
  };
  const onEdit = (p) => { setForm({ ...empty(), ...p }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API_URL}/api/products/${id}`, { withCredentials: true });
    load();
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? list.filter((p) => `${p.name} ${p.description || ''} ${p.hsn_sac || ''}`.toLowerCase().includes(q)) : list;
  }, [list, search]);

  const { paged, controls, startIdx } = usePagination(filtered, { storageKey: 'products-page-size', testIdPrefix: 'product' });

  return (
    <div data-testid="products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Products / Services</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Items you sell or bill for</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
              <Plus size={18} className="mr-1" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{edit ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
              <div><Label>Description</Label>
                <textarea className="w-full border rounded-md px-3 py-2 text-sm" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Unit Price (₹)</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="product-price" /></div>
                <div><Label>HSN / SAC Code</Label><Input value={form.hsn_sac} onChange={(e) => setForm({ ...form, hsn_sac: e.target.value })} /></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-product-btn">{edit ? 'Update' : 'Save'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        <div className="p-3 border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <Input placeholder="Search products by name, description, HSN…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="product-search" />
        </div>
        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead>#</TableHead><TableHead>NAME</TableHead><TableHead>DESCRIPTION</TableHead><TableHead>HSN/SAC</TableHead><TableHead>PRICE</TableHead><TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>{list.length === 0 ? 'No products yet.' : 'No matches.'}</TableCell></TableRow>
            ) : paged.map((p, i) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="text-gray-400">{startIdx + i + 1}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell style={{ color: 'var(--dx-text2)' }}>{p.description || '-'}</TableCell>
                <TableCell>{p.hsn_sac || '-'}</TableCell>
                <TableCell className="font-semibold">₹{Number(p.unit_price || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(p)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(p.id)}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};

export default ProductsPage;
